const {
    keys, find, forEach, endsWith, union,
} = require('lodash');

class RichRelevanceService {
    constructor(restangular, $window, $q, rootScope, $http, $timeout, config, shopperService, featureService, settings, breakpointsService, analyticsService) {
        this._api = restangular.one('RichRelevance');
        this.placementCallbacks = {};
        this.placementsCache = {};
        this.$window = $window;
        this.config = config;
        this.shopperService = shopperService;
        this.shopper = this.shopperService.shopper;
        this.$q = $q;
        this.$http = $http;
        this.$timeout = $timeout;
        this.features = featureService;
        this.settings = settings;
        this.breakpointsService = breakpointsService;
        this.analyticsService = analyticsService;
        this.currentProducts = [];
        this.placementTimeouts = {};
        this.maxPlacementDisplayDelay = 0;
        this.homepagePlacement = 'home_page.carousel';
        this.quickReOrderPlacement = 'generic_page.quick_reorder';
        this.specialsGroupPlacement = 'category_page.specials_group';
        this.mySpecialsPlacement = 'category_page.trader_myspecials';
        this.mySpecialsPlacementMobile = 'category_page.trader_myspecials_mobile';
        this.categoryPlacement = 'category_page.vs_category';
        this.categoryLevelOne = 'category_page.category_level1';
        this.categoryLevelTwo = 'category_page.category_level2';
        this.categoryLevelThree = 'category_page.category_level3';
        this.itemPageRankOne = 'item_page.rank1';
        this.itemPageRankTwo = 'item_page.rank2';
        this.mySpecialsGroupId = 'specialsgroup.-1';
        this.rrSourceName = 'RR';

        this.initPromise = null;

        this.serverSideApiPlacements = [
            this.homepagePlacement,
            this.quickReOrderPlacement,
            this.categoryPlacement,
            this.specialsGroupPlacement,
            this.mySpecialsPlacement,
            this.categoryLevelOne,
            this.categoryLevelTwo,
            this.categoryLevelThree,
            this.itemPageRankOne,
            this.itemPageRankTwo,
            `${this.homepagePlacement}_mobile`,
            `${this.quickReOrderPlacement}_mobile`,
            `${this.categoryPlacement}_mobile`,
            `${this.specialsGroupPlacement}_mobile`,
            `${this.mySpecialsPlacement}_mobile`,
            `${this.categoryLevelOne}_mobile`,
            `${this.categoryLevelTwo}_mobile`,
            `${this.categoryLevelThree}_mobile`,
            `${this.itemPageRankOne}_mobile`,
            `${this.itemPageRankTwo}_mobile`,
        ];

        rootScope.$on('$stateChangeSuccess', () => {
            // when the stateChange happens,
            // placements in cache are no longer valid.
            this.placementCallbacks = {};
            this.placementsCache = {};
            this.currentProducts = [];
            this.placementTimeouts = {};
        });
    }

    getInformation() {
        if (!this.features.isEnabled(this.features.features.enableRichRelevanceRecommendations) || !this.$window.RR) {
            return this.$q.reject('rr is disabled');
        }

        this.cleanupR3();

        const promise = this.shopper.RichRelevanceSessionId !== undefined && this.shopper.FulfilmentStoreId !== undefined
            ? this.$q.all({ RichRelevanceSessionId: this.shopper.RichRelevanceSessionId, FulfilmentStoreId: this.shopper.FulfilmentStoreId })
            : this.shopperService.getCurrentShopper();

        return promise.then((response) => {
            window.R3_COMMON = new this.$window.r3_common(); // eslint-disable-line new-cap
            this.$window.R3_COMMON.setApiKey(this.config.RichRelevanceApiKey);
            this.$window.R3_COMMON.setBaseUrl(window.location.protocol + this.config.RichRelevanceRequestUrl);
            this.$window.R3_COMMON.setClickthruServer(`${window.location.protocol}//${window.location.host}`);
            this.$window.R3_COMMON.setSessionId(response.RichRelevanceSessionId);
            this.$window.R3_COMMON.setUserId(this.shopperService.IsGuest ? '' : this.shopper.Id); // if no user ID is available, please leave this blank
            this.$window.R3_COMMON.setRegionId(response.FulfilmentStoreId);

            return this.$window.R3_COMMON;
        });
    }

    recordPlacementsRequested(placementId) {
        this.placementTimeouts[placementId] = new Date().getTime() + this.settings.richRelevance.maxPlacementDisplayDelay * 1000;
    }

    onPlacementsReceived(placements) {
        // if server side api is on and placement has products, add callbacks.
        if (this.useServerSideResults(placements)) {
            if (!placements || !placements.Products) {
                return;
            }

            const currentPlacementkeys = keys(this.placementCallbacks);

            if (placements && currentPlacementkeys) {
                // only fire callback function when placement name is in both serverSideApiPlacements and placementCallbacks keys
                if (this.serverSideApiPlacements.indexOf(placements.placement_name) >= 0 && currentPlacementkeys.indexOf(placements.placement_name) >= 0) {
                    this.currentProducts = union(this.currentProducts, placements.Items);
                    this.placementCallbacks[placements.placement_name](placements);
                }
            }
            return;
        }

        const receiveTime = new Date().getTime();
        forEach(
            placements,
            (placement) => {
                if (!placement || placement == null) return;
                const timeout = this.placementTimeouts[placement.placement_name];
                if (placement.placement_name in this.placementCallbacks) {
                    if (!timeout || timeout >= receiveTime) {
                        this.placementCallbacks[placement.placement_name](placement);
                        this.currentProducts = this.currentProducts.concat(placement.items);
                        this.placementsCache[placement.placement_name] = placement;
                    }
                    if (timeout) {
                        this.analyticsService.push({
                            data: {
                                event: 'richRelevanceResponse',
                                eventCategory: 'RichRelevance',
                                eventLabel: 'CarouselDisplay',
                                eventValue: timeout >= receiveTime ? 'Y' : 'N',
                            },
                            type: this.analyticsService.mapperTypes.seo.pageMetadata,
                        });
                        if (timeout < receiveTime) {
                            this.analyticsService.pushEvent(this.analyticsService.events.RICH_RELEVANCE_TIMEOUT);
                        }
                    }
                }
                this.placementTimeouts[placement.placement_name] = null;
            },
        );
    }

    // registers a callback to be called when placements are received.
    // Callback must handle being called with no data or null, as this can happen
    // if placements are cleared. see clearPlacements();
    registerPlacementCallback(placement, callback) {
        this.placementCallbacks[placement] = callback;
        if (this.placementsCache[placement]) {
            callback(this.placementsCache[placement]);
        } else if (this.placementsCache[this.getOriginalPlacementId(placement)]) {
            // When change LF to SF or SF to LF, make sure call callback method, even if placement has changed.
            callback(this.placementsCache[this.getOriginalPlacementId(placement)]);
        }
    }

    addToTrolleyRequest(product, trolleyProducts) {
        this.getInformation()
            .then(() => {
                const addToCartRequest = this.$timeout(() => {
                    this.getInformation().then(() => { this.rrAddToCartPageRequest(product.Stockcode, product.Source); });
                }, 100);

                addToCartRequest.then(() => {
                    this.$timeout(() => {
                        this.getInformation().then(() => { this.rrCartPageRequest(trolleyProducts); });
                    }, 100);
                });
            });
    }

    rrCartPageRequest(trolleyProducts) {

        if (!trolleyProducts || !trolleyProducts.length) {
            return;
        }

        this.$window.R3_CART = new this.$window.r3_cart(); // eslint-disable-line new-cap

        forEach(trolleyProducts, (product) => {
            this.$window.R3_CART.addItemId(product.Stockcode.toString());
        });

        this.$window.rr_flush_onload();
        this.$window.r3();

    }

    rrItemPageRequest(product) {
        this.$window.R3_ITEM = new window.r3_item(); // eslint-disable-line new-cap
        this.$window.R3_ITEM.setId(product.Stockcode.toString()); // This must pass a string value, as RR calls indexOf() on this internally, possibly to URL encode it.
        this.$window.R3_ITEM.setName(product.Name);

        this.$window.rr_flush_onload();
        this.$window.r3();
    }

    rrItemDetailsPageRequest(stockcode, source) {
        const product = find(this.currentProducts, { id: stockcode.toString() });
        const isRRitem = (source || '').indexOf(this.rrSourceName) !== -1;
        if (product && product.linkURL && isRRitem) {
            this.$http.get(product.linkURL);
        }
    }

    rrAddToCartPageRequest(stockcode, source) {
        this.$window.R3_ADDTOCART = new window.r3_addtocart(); // eslint-disable-line new-cap
        this.$window.R3_ADDTOCART.addItemIdToCart(stockcode);

        this.$window.rr_flush_onload();
        this.$window.r3();

        this.rrItemDetailsPageRequest(stockcode, source);
    }

    clearPlacements() {
        forEach(this.placementCallbacks, (callback) => {
            // call each callback with null;
            callback(null);
        });
    }

    cleanupR3() {
        // RR script checks for the existance of these objects
        // to decide what to send to their server.
        // They use the typeof operator
        // eg: if (typeof window.R3_COMMON !== "undefined" )
        // rather than if (!window.R3_*) so null or false would not work.

        // R3_COMMON also needs to be cleared, as placement
        // types are registered on it. (R3_COMMON.addPlacementType(...))
        // So when the page changes R3_COMMON needs to be reinitialized
        // to not have the old placements.

        // Unregistering all page types including common
        window.RR.unregisterAllPageType(true);

        window.R3_COMMON = undefined;
        window.R3_HOME = undefined;
        window.R3_ADDTOCART = undefined;
        window.R3_BRAND = undefined;
        window.R3_CATEGORY = undefined;
        window.R3_GENERIC = undefined;
        window.R3_ITEM = undefined;
        window.R3_PURCHASED = undefined;
        window.R3_WISHLIST = undefined;
        window.R3_ADDTOREGISTRY = undefined;
        window.R3_CART = undefined;
        window.R3_ERROR = undefined;
        window.R3_PERSONAL = undefined;
        window.R3_SEARCH = undefined;
    }

    invalidate() {
        this.initPromise = null;
    }

    refreshPlacements() {
        forEach(this.placementsCache, (placements, placementId) => {
            const callback = this.placementCallbacks[placementId];
            if (callback) {
                callback(placements);
            }
        });
    }

    getEffectivePlacementId(placementId) {
        return this.breakpointsService.isInSmallFormat && !endsWith(placementId, '_mobile') ? `${placementId}_mobile` : placementId;
    }

    getOriginalPlacementId(placementId) {
        return this.breakpointsService.isInSmallFormat ? placementId.replace('_mobile', '') : `${placementId}_mobile`;
    }

    getPlacementIdsForSpecialsGroup(category) {
        let placementIds = this.specialsGroupPlacement;
        // When products count is less than minimum count and bank page, don't show recommendation.
        if (category.ProductCount < this.settings.richRelevance.minimumRequiredProductsInSpecialsGroup || category.Url) {
            placementIds = undefined;
        }
        return placementIds;
    }

    getPlacementByCategoryLevel(categoryLevel) {
        if (!categoryLevel) {
            return null;
        }
        switch (categoryLevel) {
            case 1:
                return this.categoryLevelOne;
            case 2:
                return this.categoryLevelTwo;
            case 3:
                return this.categoryLevelThree;
            default:
                return null;
        }
    }

    useServerSideResults(placements) {
        return this.settings.richRelevance.removeDuplicatedProducts
            || (placements && (placements.placement_name === this.mySpecialsPlacement
                || placements.placement_name === this.mySpecialsPlacementMobile));
    }
}

module.exports = RichRelevanceService;



// WEBPACK FOOTER //
// ./src/shop/richrelevance/richrelevance.service.js